import React, { useState, useEffect } from 'react';
import { Drawer, IconButton, Stack, Typography, Box, TextField, InputAdornment, Button } from '@mui/material';
import { Cancel01Icon, Call02Icon, MessageNotification01Icon, ArrowRight02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useAppDispatch, useAppSelector, useResponsive } from '@/hooks';
import { closePhoneLoginDrawer } from '@/store/uiSlice';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import LoadingButton from '@mui/lab/LoadingButton';
import InputMask from "react-input-mask";
import { sanitizePhone } from "@/utils/phones";
import { auth } from "@/firebase";
import { RecaptchaVerifier } from "firebase/auth";
import useSnackbar from "@/hooks/useSnackbar";
import { useSendOtpMutation, useConfirmOtpMutation } from "@/api/authApi";

const phoneValidationSchema = Yup.object().shape({
  phone: Yup.string()
    .length(8, 'El teléfono debe tener 8 dígitos')
    .required('Campo requerido'),
});

const otpValidationSchema = Yup.object().shape({
  otp: Yup.string()
    .length(6, 'El código debe tener 6 dígitos')
    .required('Campo requerido'),
});

const PhoneLoginDrawer: React.FC = () => {
  const dispatch = useAppDispatch();
  const [openSnackbar] = useSnackbar();
  const { isMobile } = useResponsive();
  const isOpen = useAppSelector((state) => state.ui.isPhoneLoginDrawerOpen);

  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [sendOtp, { isLoading: isSendingOtp }] = useSendOtpMutation();
  const [confirmOtp, { isLoading: isVerifyingOtp }] = useConfirmOtpMutation();

  useEffect(() => {
    if (!isOpen) {
      setStep('phone');
    }
  }, [isOpen]);

  const setupRecaptcha = () => {
    if (!(window as any).recaptchaVerifier) {
      (window as any).recaptchaVerifier = new RecaptchaVerifier('recaptcha-container', {
        'size': 'invisible',
        'callback': () => { }
      }, auth);
    }
  };

  const handleClose = () => {
    dispatch(closePhoneLoginDrawer());
  };

  const phoneFormik = useFormik({
    initialValues: { phone: '' },
    validationSchema: phoneValidationSchema,
    onSubmit: async (values) => {
      try {
        setupRecaptcha();
        const appVerifier = (window as any).recaptchaVerifier;
        const formattedPhone = `+505${values.phone}`;

        await sendOtp({ phone: formattedPhone, verifier: appVerifier }).unwrap();
        setStep('otp');
        openSnackbar("Código enviado por SMS", "success");
      } catch (error: any) {
        console.error("SMS Error:", error);
        openSnackbar(error.error || "Error al enviar SMS", "error");
        if ((window as any).recaptchaVerifier) {
          (window as any).recaptchaVerifier.clear();
          (window as any).recaptchaVerifier = null;
        }
      }
    },
  });

  const otpFormik = useFormik({
    initialValues: { otp: '' },
    validationSchema: otpValidationSchema,
    onSubmit: async (values) => {
      try {
        await confirmOtp({ code: values.otp }).unwrap();
        openSnackbar("Sesión iniciada correctamente", "success");
        handleClose();
      } catch (error: any) {
        console.error("OTP Error:", error);
        openSnackbar(error.error || "Código inválido o expirado", "error");
      }
    },
  });

  return (
    <Drawer
      anchor={isMobile ? "bottom" : "right"}
      open={isOpen}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : 450,
          p: 0,
          bgcolor: "#F4F7FE",
          borderTopLeftRadius: isMobile ? 24 : 0,
          borderTopRightRadius: isMobile ? 24 : 0,
        }
      }}
    >
      <Stack p={3} spacing={4}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h3" fontWeight={600}>
            {step === 'phone' ? 'Inicia Sesión' : 'Verifica tu Código'}
          </Typography>
          <IconButton onClick={handleClose}>
            <HugeiconsIcon icon={Cancel01Icon} />
          </IconButton>
        </Stack>

        <Box>
          <Typography variant="body1" color="text.secondary" mb={3}>
            {step === 'phone'
              ? 'Ingresa tu número de teléfono para recibir un código de acceso por SMS.'
              : 'Hemos enviado un código de 6 dígitos a tu teléfono.'}
          </Typography>

          {step === 'phone' ? (
            <form onSubmit={phoneFormik.handleSubmit}>
              <Stack spacing={3}>
                <InputMask
                  mask="9999-9999"
                  placeholder="0000-0000"
                  alwaysShowMask={false}
                  value={phoneFormik.values.phone}
                  onChange={(e: any) => phoneFormik.setFieldValue("phone", sanitizePhone(e.target.value))}
                  onBlur={phoneFormik.handleBlur}
                >
                  {() => (
                    <TextField
                      label="Teléfono"
                      error={phoneFormik.touched.phone && Boolean(phoneFormik.errors.phone)}
                      helperText={phoneFormik.touched.phone && phoneFormik.errors.phone}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <HugeiconsIcon icon={Call02Icon} size={20} />
                            <Typography sx={{ ml: 1, fontWeight: 500 }}>+505</Typography>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                </InputMask>

                <LoadingButton
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  loading={isSendingOtp}
                  endIcon={<HugeiconsIcon icon={ArrowRight02Icon} />}
                  sx={{ py: 1.5, borderRadius: 3 }}
                >
                  Enviar Código
                </LoadingButton>
              </Stack>
            </form>
          ) : (
            <form onSubmit={otpFormik.handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  label="Código de Verificación"
                  placeholder="000000"
                  fullWidth
                  value={otpFormik.values.otp}
                  onChange={otpFormik.handleChange}
                  name="otp"
                  onBlur={otpFormik.handleBlur}
                  error={otpFormik.touched.otp && Boolean(otpFormik.errors.otp)}
                  helperText={otpFormik.touched.otp && otpFormik.errors.otp}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <HugeiconsIcon icon={MessageNotification01Icon} size={20} />
                      </InputAdornment>
                    ),
                  }}
                />

                <LoadingButton
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  loading={isVerifyingOtp}
                  color="success"
                  sx={{ py: 1.5, borderRadius: 3 }}
                >
                  Verificar y Entrar
                </LoadingButton>

                <Button
                  variant="text"
                  onClick={() => setStep('phone')}
                  disabled={isVerifyingOtp}
                >
                  Cambiar número
                </Button>
              </Stack>
            </form>
          )}
        </Box>
      </Stack>

      <div id="recaptcha-container"></div>
    </Drawer>
  );
};

export default PhoneLoginDrawer;
